/**
 * What a game registers for rooms and a map's atmosphere (ADR-122 decisions 2.2, 3.1 and 6): the zone type a
 * new room's floor is painted with, the entity type it treats as an arrival point, and the music and ambience names a map's atmosphere may take, each a folder path
 * under the game's `music/` or `ambience/`. Daedalus reads only the ids and the names; what they sound like is
 * the game's.
 */

/**
 * The music and ambience names a game lists.
 */
export interface AtmosphereLists {
  readonly music: readonly string[];
  readonly ambience: readonly string[];
}

/**
 * A folder path: lower-case segments of letters, digits and `-` joined by `/`.
 */
const FOLDER_PATH = /^[a-z0-9-]+(\/[a-z0-9-]+)*$/;

/**
 * Checks a game's atmosphere lists: each name a folder path, none listed twice.
 *
 * @param lists The lists.
 * @throws Error naming the list and the name.
 */
export function validateAtmosphere(lists: AtmosphereLists): void {
  for (const key of ["music", "ambience"] as const) {
    const seen = new Set<string>();
    for (const name of lists[key]) {
      if (!FOLDER_PATH.test(name)) {
        throw new Error(`atmosphere.${key}: ${name} is not a folder path of lower-case letters, digits and - between /`);
      }
      if (seen.has(name)) {
        throw new Error(`atmosphere.${key}: ${name} is listed twice`);
      }
      seen.add(name);
    }
  }
}

/**
 * Checks that the zone type a new room's floor is painted with is one the game registers.
 *
 * @param roomZone The zone type's id, or undefined when the game names none.
 * @param zoneTypes The ids of the zone types the game registers.
 * @throws Error naming the id when it is not registered.
 */
export function validateRoomZone(roomZone: string | undefined, zoneTypes: readonly string[]): void {
  if (roomZone !== undefined && !zoneTypes.includes(roomZone)) {
    throw new Error(`roomZone ${roomZone} names no registered zone type`);
  }
}

/**
 * Checks that the entity type a game treats as an arrival point is one it registers (ADR-122 decision 2.2).
 *
 * @param arrivalType The type, or undefined when the game names none.
 * @param entityTypes The entity types the game registers.
 * @throws Error naming the type when it is not registered.
 */
export function validateArrivalType(arrivalType: string | undefined, entityTypes: readonly string[]): void {
  if (arrivalType !== undefined && !entityTypes.includes(arrivalType)) {
    throw new Error(`arrivalType ${arrivalType} names no registered entity type`);
  }
}

/**
 * A game's room fields: the room zone, the atmosphere lists and the arrival type, each present only when
 * registered.
 */
export interface RoomFields {
  readonly roomZone?: string;
  readonly atmosphere?: AtmosphereLists;
  readonly arrivalType?: string;
}

/**
 * The room fields to spread into a contract or an adapter: each present only when the source registers it, so
 * a game that registers neither writes the contract it did.
 *
 * @param source The adapter or the contract.
 * @returns The fields.
 */
export function roomFieldsOf(source: RoomFields): RoomFields {
  return {
    ...(source.roomZone === undefined ? {} : { roomZone: source.roomZone }),
    ...(source.atmosphere === undefined ? {} : { atmosphere: { music: [...source.atmosphere.music], ambience: [...source.atmosphere.ambience] } }),
    ...(source.arrivalType === undefined ? {} : { arrivalType: source.arrivalType }),
  };
}

/**
 * Reads a contract's room fields and checks them as a registration does.
 *
 * @param record The contract's fields.
 * @param zoneTypes The ids of the zone types the contract registers.
 * @param entityTypes The entity types the contract registers.
 * @returns The fields present.
 * @throws Error naming the field when the room zone or the arrival type is not text or names no registered type,
 *   or the lists are malformed.
 */
export function readRoomFields(record: Record<string, unknown>, zoneTypes: readonly string[], entityTypes: readonly string[]): RoomFields {
  const roomZone = record["roomZone"];
  const arrivalType = record["arrivalType"];
  if (roomZone !== undefined && typeof roomZone !== "string") {
    throw new Error("roomZone must be a string");
  }
  if (arrivalType !== undefined && typeof arrivalType !== "string") {
    throw new Error("arrivalType must be a string");
  }
  validateRoomZone(roomZone, zoneTypes);
  validateArrivalType(arrivalType, entityTypes);
  const named = { ...(roomZone === undefined ? {} : { roomZone }), ...(arrivalType === undefined ? {} : { arrivalType }) };
  const lists = record["atmosphere"];
  if (lists === undefined) {
    return roomFieldsOf(named);
  }
  const names = (key: "music" | "ambience"): string[] => {
    const list = (lists as Record<string, unknown> | null)?.[key];
    if (!Array.isArray(list) || !list.every((name): name is string => typeof name === "string")) {
      throw new Error(`atmosphere.${key} must be a list of names`);
    }
    return list;
  };
  const atmosphere = { music: names("music"), ambience: names("ambience") };
  validateAtmosphere(atmosphere);
  return roomFieldsOf({ ...named, atmosphere });
}
